/**
 * Reine Logik der oeffentlichen Einweisungsseite (#/einweisung/<linkToken>).
 *
 * Kein React, kein Supabase: Schrittfolge, Formularpruefung, Lese-Regel,
 * sessionStorage-Schluessel und -Serialisierung, Zeitformat. Alles, was die
 * Seite an Entscheidungen trifft, steht hier und ist in
 * einweisungTeilnahmeLogic.test.ts abgedeckt — die Komponenten bleiben duenn
 * (kein jsdom im Test). Vorbild: components/sifa/public/teilnahmeSeiteLogic.ts.
 *
 * Bewusst NICHT hier: `istTouchGeraet` und `UNTERSCHRIFT_MAX_BREITE` — die
 * kommen aus der SiFa-Teilnehmerseite und werden dort weiterverwendet.
 */

/** Die Seite hat genau diese Schritte in dieser Reihenfolge. */
export type Schritt = 'begruessung' | 'inhalt' | 'formular' | 'unterschrift' | 'ergebnis';

export const SCHRITTE: readonly Schritt[] = ['begruessung', 'inhalt', 'formular', 'unterschrift', 'ergebnis'];

/**
 * Mindestlesezeit des Inhalts, bevor „Weiter" ohne Scroll-Ende frei wird.
 * Laenger als bei der Unterweisung (10 s je Abschnitt): das Blatt steht auf
 * EINER Seite, und wer die Stichpunkte in 20 s ueberfliegt, hat sie gesehen.
 */
export const MINDESTLESEZEIT_MS = 20_000;

/** Toleranz in Pixeln, ab der ein Scrollstand als „ganz unten" gilt. */
export const SCROLL_TOLERANZ_PX = 8;

/** Laengen aus dem CHECK der Tabelle sige_einweisung_teilnahmen (Migration Nr. 97). */
export const MAX_VORNAME = 80;
export const MAX_NACHNAME = 80;
export const MAX_FUNKTION = 80;
export const MAX_FIRMA = 120;

export const schrittIndex = (s: Schritt): number => Math.max(0, SCHRITTE.indexOf(s));

/**
 * Fortschritt in Prozent fuer die Leiste: vier Stationen (Begruessung,
 * Inhalt, Formular, Unterschrift), das Ergebnis ist die volle Leiste.
 */
export const fortschrittProzent = (s: Schritt): number =>
  Math.round((schrittIndex(s) * 100) / (SCHRITTE.length - 1));

/** Text rechts ueber der Leiste: „Schritt 2 von 4", beim Ergebnis „Fertig". */
export const schrittText = (s: Schritt): string =>
  s === 'ergebnis' ? 'Fertig' : `Schritt ${schrittIndex(s) + 1} von ${SCHRITTE.length - 1}`;

export const naechsterSchritt = (s: Schritt): Schritt =>
  SCHRITTE[Math.min(SCHRITTE.length - 1, schrittIndex(s) + 1)];

export const vorherigerSchritt = (s: Schritt): Schritt => SCHRITTE[Math.max(0, schrittIndex(s) - 1)];

const istSchritt = (v: unknown): v is Schritt => typeof v === 'string' && (SCHRITTE as readonly string[]).includes(v);

// ─── Formular ────────────────────────────────────────────────────────────────

/** Eingaben der Person (Betreiber 12.09.: kein Geburtsdatum, keine E-Mail). */
export interface TeilnahmeFormular {
  vorname: string;
  nachname: string;
  /** Freitext, darf leer bleiben — z. B. Polier, Bauleiter, Monteur. */
  funktion: string;
  firma: string;
  /** Pflicht-Haekchen „Ich habe die Einweisung … erhalten und verstanden." */
  verstanden: boolean;
}

export const LEERES_FORMULAR: TeilnahmeFormular = {
  vorname: '',
  nachname: '',
  funktion: '',
  firma: '',
  verstanden: false,
};

export type FormularFehler = Partial<Record<keyof TeilnahmeFormular, string>>;

/**
 * Prueft je Feld und liefert die Meldung zum Feld. Die Grenzen sind die
 * CHECKs der Tabelle — was hier durchgeht, nimmt die RPC an (22023 bleibt
 * ein Notausgang fuer den Fall, dass der Client veraltet ist).
 */
export const pruefeFormularFelder = (f: TeilnahmeFormular): FormularFehler => {
  const fehler: FormularFehler = {};
  const vorname = f.vorname.trim();
  const nachname = f.nachname.trim();
  const firma = f.firma.trim();
  const funktion = f.funktion.trim();
  if (!vorname) fehler.vorname = 'Bitte den Vornamen eintragen.';
  else if (vorname.length > MAX_VORNAME) fehler.vorname = `Der Vorname darf höchstens ${MAX_VORNAME} Zeichen lang sein.`;
  if (!nachname) fehler.nachname = 'Bitte den Nachnamen eintragen.';
  else if (nachname.length > MAX_NACHNAME) fehler.nachname = `Der Nachname darf höchstens ${MAX_NACHNAME} Zeichen lang sein.`;
  if (!firma) fehler.firma = 'Bitte die Firma eintragen.';
  else if (firma.length > MAX_FIRMA) fehler.firma = `Die Firma darf höchstens ${MAX_FIRMA} Zeichen lang sein.`;
  if (funktion.length > MAX_FUNKTION) fehler.funktion = `Die Funktion darf höchstens ${MAX_FUNKTION} Zeichen lang sein.`;
  if (!f.verstanden) fehler.verstanden = 'Bitte bestätige, dass du die Einweisung erhalten und verstanden hast.';
  return fehler;
};

/** Alle Meldungen in Feldreihenfolge; leer = Formular in Ordnung. */
export const pruefeFormular = (f: TeilnahmeFormular): string[] => {
  const fehler = pruefeFormularFelder(f);
  const reihenfolge: (keyof TeilnahmeFormular)[] = ['vorname', 'nachname', 'firma', 'funktion', 'verstanden'];
  return reihenfolge.flatMap((k) => (fehler[k] ? [fehler[k] as string] : []));
};

/** Eingaben, wie sie an die RPC gehen: getrimmt, Funktion leer → ''. */
export const formularBereinigt = (f: TeilnahmeFormular): TeilnahmeFormular => ({
  vorname: f.vorname.trim(),
  nachname: f.nachname.trim(),
  funktion: f.funktion.trim(),
  firma: f.firma.trim(),
  verstanden: f.verstanden,
});

/** „Max Muster" fuer die Ergebniskarte. */
export const vollerName = (f: Pick<TeilnahmeFormular, 'vorname' | 'nachname'>): string =>
  `${f.vorname.trim()} ${f.nachname.trim()}`.trim();

/**
 * Das Handy wird weitergereicht: Name und Haekchen sind weg, Firma und
 * Funktion bleiben — die naechste Person ist meistens aus derselben Kolonne
 * (wie im Reihum-Modus der Geraete-Erfassung).
 */
export const naechstePerson = (f: TeilnahmeFormular): TeilnahmeFormular => ({
  vorname: '',
  nachname: '',
  funktion: f.funktion,
  firma: f.firma,
  verstanden: false,
});

// ─── Lese-Regel ──────────────────────────────────────────────────────────────

/**
 * „Weiter" wird frei, sobald bis zum Ende gescrollt wurde ODER die
 * Mindestlesezeit um ist.
 */
export const darfWeiterNachLesen = (scrollEnde: boolean, msGelesen: number): boolean =>
  scrollEnde || (Number.isFinite(msGelesen) && msGelesen >= MINDESTLESEZEIT_MS);

/**
 * Scroll-Ende aus den Massen des Dokuments. Passt der Inhalt ohne Scrollen
 * auf den Bildschirm, gilt das NICHT als Scroll-Ende — sonst waere der Knopf
 * bei kurzen Blaettern sofort frei und die Lesepflicht leer; dann zaehlt nur
 * die Zeit.
 */
export const istScrollEnde = (scrollTop: number, sichtbareHoehe: number, gesamtHoehe: number): boolean => {
  const ueberlauf = gesamtHoehe > sichtbareHoehe + SCROLL_TOLERANZ_PX;
  if (!ueberlauf) return false;
  return scrollTop + sichtbareHoehe >= gesamtHoehe - SCROLL_TOLERANZ_PX;
};

/** Verbleibende Sekunden bis zur Zeitfreigabe, nie negativ. */
export const restSekunden = (msGelesen: number): number =>
  Math.max(0, Math.ceil((MINDESTLESEZEIT_MS - (Number.isFinite(msGelesen) ? msGelesen : 0)) / 1000));

// ─── Anzeige ─────────────────────────────────────────────────────────────────

/** Ueberschrift und Hinweis des Unterschriftenfelds (Handy-Vollbild und PC-Overlay gleich). */
export const UNTERSCHRIFT_TITEL = 'Unterschrift — Einweisung in den SiGe-Plan';
export const UNTERSCHRIFT_HINWEIS =
  'Bitte im Feld unterschreiben und übernehmen. Die Unterschrift steht in der Teilnehmerliste der Einweisung.';

/** ISO-Zeitstempel → „12.09.2026, 07:41" in Ortszeit; Unlesbares unveraendert. */
export const formatZeitpunkt = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const zwei = (n: number) => String(n).padStart(2, '0');
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}, ${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
};

/** YYYY-MM-DD → DD.MM.YYYY ohne Zeitzonen-Umweg; alles andere unveraendert. */
export const formatDatum = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

/**
 * Meldungen zu den SQLSTATEs der RPC `sige_einweisung_teilnahme_abschliessen`.
 * P0002 (Link unbekannt/abgelaufen/Blatt nicht abgeschlossen) behandelt der
 * Hook als „Link ungueltig" — hier nur die Texte fuer den Fehlerkasten.
 */
export const fehlerTextZuCode = (code: string | null, fallback: string): string => {
  switch (code) {
    case '54000':
      return 'Gerade tragen sich sehr viele Personen gleichzeitig ein. Bitte in ein paar Minuten erneut versuchen.';
    case '22023':
      return 'Die Eingaben wurden nicht angenommen. Bitte Name, Firma und Unterschrift prüfen.';
    default:
      return fallback || 'Unbekannter Fehler';
  }
};

// ─── sessionStorage ──────────────────────────────────────────────────────────

/** Rueckgabe der RPC, wie die Ergebniskarte sie braucht. */
export interface TeilnahmeErgebnis {
  bereits: boolean;
  /** ISO-Zeitstempel. */
  unterschriebenAm: string;
  /** „Max Muster" — aus dem Formular beim Abschluss. */
  name: string;
}

/**
 * Alles, was ein Neuladen ueberleben muss. Die Unterschrift ist NIE dabei —
 * die DataURL geht sofort an die RPC und wird nirgends zwischengespeichert.
 */
export interface TeilnahmeZustand {
  version: 1;
  schritt: Schritt;
  formular: TeilnahmeFormular;
  ergebnis: TeilnahmeErgebnis | null;
}

export const LEERER_ZUSTAND: TeilnahmeZustand = {
  version: 1,
  schritt: 'begruessung',
  formular: LEERES_FORMULAR,
  ergebnis: null,
};

export const speicherSchluessel = (linkToken: string): string => `osia.einweisung.online.${linkToken}`;

/** Nur die vier Felder — ein versehentlich angehaengtes Bild faellt weg. */
export const serialisiereZustand = (z: TeilnahmeZustand): string =>
  JSON.stringify({
    version: 1,
    schritt: z.schritt,
    formular: {
      vorname: z.formular.vorname,
      nachname: z.formular.nachname,
      funktion: z.formular.funktion,
      firma: z.formular.firma,
      verstanden: z.formular.verstanden === true,
    },
    ergebnis: z.ergebnis
      ? { bereits: z.ergebnis.bereits === true, unterschriebenAm: z.ergebnis.unterschriebenAm, name: z.ergebnis.name }
      : null,
  } satisfies TeilnahmeZustand);

const istString = (v: unknown): v is string => typeof v === 'string';

/**
 * Liest einen gespeicherten Zustand tolerant: kaputtes JSON, fremde Version
 * oder fehlende Felder ergeben null bzw. Standardwerte. Ein Ergebnis-Schritt
 * ohne Ergebnis faellt aufs Formular zurueck; der Unterschrift-Schritt
 * ebenfalls (die Unterschrift wird nie gespeichert, also faengt sie neu an —
 * sauberer als eine leere Unterschriftenkarte nach dem Neuladen).
 */
export const parseZustand = (raw: string | null | undefined): TeilnahmeZustand | null => {
  if (!raw) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const z = obj as Record<string, unknown>;
  if (z.version !== 1) return null;
  const f = (z.formular && typeof z.formular === 'object' ? z.formular : {}) as Record<string, unknown>;
  const e = (z.ergebnis && typeof z.ergebnis === 'object' ? z.ergebnis : null) as Record<string, unknown> | null;
  const ergebnis: TeilnahmeErgebnis | null =
    e && istString(e.unterschriebenAm) && istString(e.name)
      ? { bereits: e.bereits === true, unterschriebenAm: e.unterschriebenAm, name: e.name }
      : null;
  let schritt: Schritt = istSchritt(z.schritt) ? z.schritt : 'begruessung';
  if (schritt === 'ergebnis' && !ergebnis) schritt = 'formular';
  if (schritt === 'unterschrift') schritt = 'formular';
  return {
    version: 1,
    schritt,
    formular: {
      vorname: istString(f.vorname) ? f.vorname : '',
      nachname: istString(f.nachname) ? f.nachname : '',
      funktion: istString(f.funktion) ? f.funktion : '',
      firma: istString(f.firma) ? f.firma : '',
      verstanden: f.verstanden === true,
    },
    ergebnis,
  };
};
